/**
 * geometriaDeFirmas.js — las cuentas de dónde cae cada recuadro de firma.
 *
 * Está aparte de `VisorDePdf.js` por una razón práctica: ese archivo importa
 * React y el tema, así que no se puede cargar desde una prueba de Node. Esto sí,
 * y es justo lo que conviene probar, porque son las cuentas que, mal hechas, no
 * fallan: ponen la firma en otro sitio. Es el mismo motivo por el que existe
 * `vistaDelDocumento.js`.
 *
 * ── El contrato, que gobierna las dos puntas del proceso ─────────────────
 *
 * Todo lo que sale de aquí va en FRACCIONES de 0 a 1 del tamaño de su página,
 * con el origen ARRIBA a la izquierda, como mide el navegador.
 *
 * No son píxeles a propósito: el PDF se dibuja al ancho que haya —un móvil, una
 * ventana estrecha, media pantalla— y un píxel aquí no significa nada allí. En
 * fracciones, el 0,5/0,5 es el centro de la hoja en cualquier pantalla y
 * también en el PDF final.
 *
 * Quien las convierte a puntos es `azure-functions/v2/firmas/FirmaEnPdf.js`, y
 * allí es donde se le da la vuelta al origen: el PDF mide desde la esquina
 * INFERIOR. Aquí nunca.
 */

/**
 * El tamaño de un recuadro recién puesto.
 *
 * Una firma ocupa alrededor de un tercio del ancho útil y poco alto: es la
 * proporción de `LienzoDeFirma`, para que lo que alguien dibuja se parezca a lo
 * que acaba impreso.
 */
export const ANCHO_NUEVO = 0.28;
export const ALTO_NUEVO = 0.075;

/** Lo mínimo que se puede dejar un recuadro al encogerlo. */
export const MINIMO = 0.04;

/**
 * Pasa un punto del puntero a fracciones de la página.
 *
 * Se mide contra el rectángulo REAL que la hoja ocupa en pantalla, no contra el
 * tamaño del lienzo: el lienzo tiene más píxeles que tamaño —se dibuja a la
 * resolución del dispositivo— y mezclar las dos cosas pondría la firma a la
 * mitad de distancia en las pantallas buenas y en el sitio correcto en las
 * malas, que es la clase de fallo que solo le pasa a algunos.
 *
 * @returns {{x:number,y:number}|null} null si la hoja aún no tiene tamaño
 */
export function fraccionDelClic(evento, elemento) {
  if (!evento || !elemento || !elemento.getBoundingClientRect) return null;
  const caja = elemento.getBoundingClientRect();
  if (!caja.width || !caja.height) return null;
  return {
    x: (evento.clientX - caja.left) / caja.width,
    y: (evento.clientY - caja.top) / caja.height
  };
}

/**
 * Un recuadro nuevo centrado en el punto donde se pulsó.
 *
 * Sale siempre dentro de los bordes de la hoja. No es cosmético: un recuadro
 * que sobresale lo rechaza la restricción de la tabla, así que el envío entero
 * se iría abajo —con el correo ya preparado—, y si llegara a pasar produciría
 * una firma medio cortada en el PDF final.
 */
export function recuadroEn(punto, ancho = ANCHO_NUEVO, alto = ALTO_NUEVO) {
  const metido = (v, tamano) => Math.min(Math.max(v, 0), Math.max(0, 1 - tamano));
  return {
    x: metido(punto.x - ancho / 2, ancho),
    y: metido(punto.y - alto / 2, alto),
    ancho,
    alto
  };
}

/**
 * Deja un recuadro dentro de la hoja después de arrastrarlo o agrandarlo.
 *
 * Primero se limita el tamaño y DESPUÉS la posición, en ese orden: al revés, un
 * recuadro agrandado más allá del borde se recolocaría con su tamaño viejo y
 * acabaría sobresaliendo igualmente.
 */
export function encerrar(c) {
  const ancho = Math.min(Math.max(c.ancho, MINIMO), 1);
  const alto = Math.min(Math.max(c.alto, MINIMO), 1);
  return {
    ...c,
    ancho,
    alto,
    x: Math.min(Math.max(c.x, 0), 1 - ancho),
    y: Math.min(Math.max(c.y, 0), 1 - alto)
  };
}

/**
 * Reparte un recuadro por todas las hojas del documento.
 *
 * «Una firma por documento o por cada hoja le vas poniendo en qué parte va la
 * firma»: esto es lo segundo. Se coloca una vez y se repite en el mismo punto
 * de cada hoja, que es lo que se hace cuando hay que rubricar todas.
 */
export function enTodasLasHojas(recuadro, total) {
  const hojas = Math.max(0, Math.floor(total) || 0);
  const salida = [];
  for (let p = 1; p <= hojas; p++) salida.push({ ...recuadro, pagina: p });
  return salida;
}

/**
 * Si dos recuadros se pisan.
 *
 * Dos firmas en el mismo sitio se estampan una encima de la otra y las dos
 * quedan ilegibles. No se prohíbe —quien manda puede tener sus razones— pero se
 * avisa, porque sobre la pantalla dos recuadros superpuestos se ven como uno.
 */
export function sePisan(a, b) {
  if (a.pagina !== b.pagina) return false;
  return a.x < b.x + b.ancho && b.x < a.x + a.ancho
    && a.y < b.y + b.alto && b.y < a.y + a.alto;
}

/** Los pares de recuadros que se pisan, para poder decirlo. */
export function solapados(recuadros = []) {
  const pares = [];
  for (let i = 0; i < recuadros.length; i++) {
    for (let j = i + 1; j < recuadros.length; j++) {
      if (sePisan(recuadros[i], recuadros[j])) pares.push([i, j]);
    }
  }
  return pares;
}

/**
 * Quién se ha quedado sin sitio donde firmar.
 *
 * Es la comprobación que de verdad importa antes de enviar: una ronda abierta
 * con un firmante sin recuadro le manda el correo igual, esa persona abre el
 * acta y no encuentra dónde firmar. Y el PDF final sale sin su firma sin que
 * nada lo señale.
 *
 * @param {Array<{correo:string, nombre?:string}>} firmantes
 * @param {Array<{correo:string}>} recuadros
 */
export function sinRecuadro(firmantes = [], recuadros = []) {
  const tienen = new Set(
    recuadros.map(c => String((c && c.correo) || '').trim().toLowerCase()).filter(Boolean)
  );
  return firmantes.filter(f => {
    const correo = String((f && f.correo) || '').trim().toLowerCase();
    return correo && !tienen.has(correo);
  });
}

/**
 * Lo más que se amplía una hoja al dibujarla, sobre su tamaño natural.
 *
 * Dibujar a los píxeles exactos del hueco deja el texto con los bordes dentados
 * en pantallas de alta densidad, y es texto que alguien está leyendo para
 * decidir si lo firma. Lo suyo sería multiplicar por `devicePixelRatio`.
 *
 * Pero multiplicar SIN TOPE es lo que hacía que el visor tardara un mundo: en
 * una ventana de 1180 px la escala ya va por 1,9, y por una densidad de 2 sale
 * un lienzo de más de seis megapíxeles POR HOJA —segundos de trabajo cada vez
 * que se pasa de página, y la pantalla quieta mientras—.
 *
 * 2,2 veces el tamaño natural basta para que se vea limpio a pantalla completa
 * y deja el lienzo en algo más de un megapíxel.
 */
export const TOPE_RESOLUCION = 2.2;

/**
 * Las dos escalas con las que se dibuja una hoja.
 *
 * `css` es a qué tamaño se VE; `pintado` a cuántos píxeles se dibuja. Separarlas
 * es lo que permite tener texto nítido sin pagarlo en tiempo.
 *
 * @param {{anchoDisponible:number, anchoNatural:number, densidad?:number}} medidas
 * @returns {{css:number, pintado:number}}
 */
export function escalasDeLaHoja({
  anchoDisponible, anchoNatural,
  // Para ver la hoja ENTERA hace falta saber también lo alto que es el hueco.
  altoDisponible = 0, altoNatural = 0,
  densidad = 1,
  // 'ancho' llena el hueco a lo ancho y obliga a desplazarse para ver el resto.
  // 'hoja' mete la página entera, que es lo que hace falta para COLOCAR algo:
  // una firma va casi siempre abajo, y con el ajuste al ancho esa parte queda
  // fuera de la pantalla. Se tardó en ver porque desde arriba todo parecía
  // bien: el documento salía, solo que no se llegaba al sitio donde firmar.
  modo = 'ancho',
  // Lo que el usuario haya acercado o alejado a mano, sobre el ajuste elegido.
  zoom = 1
}) {
  if (!anchoNatural || anchoNatural <= 0) return { css: 1, pintado: 1 };

  const porAncho = anchoDisponible / anchoNatural;
  const cabeEntera = (modo === 'hoja' && altoDisponible > 0 && altoNatural > 0)
    ? Math.min(porAncho, altoDisponible / altoNatural)
    : porAncho;

  // El tope de 2 es para que una hoja diminuta no se amplíe hasta pixelarse;
  // el suelo, para que nunca llegue a cero y desaparezca.
  const css = Math.min(Math.max(cabeEntera * zoom, 0.1), 3);
  const limpia = Math.min(Math.max(densidad, 1), 2);
  return { css, pintado: Math.min(css * limpia, TOPE_RESOLUCION) };
}

const geometria = {
  ANCHO_NUEVO, ALTO_NUEVO, MINIMO,
  fraccionDelClic, recuadroEn, encerrar, enTodasLasHojas, sePisan, solapados, sinRecuadro,
  TOPE_RESOLUCION, escalasDeLaHoja
};
export default geometria;
